import React from 'react';
import { useTranslation } from '../../utils/languageUtils';
import './DeliverPrintExportPanel.css';

const DeliverPrintExportPanel = ({
  exportedImages,
  showExportedImages,
  setShowExportedImages,
  allViewsImages = [],
  configData,
  onAddToCart,
  isTestMode = true,
  isExporting = false,
  statusMessage = ''
}) => {
  const { t, currentLang } = useTranslation();

  const isKiosk = React.useMemo(() => {
    try {
      if (typeof document !== 'undefined' && document.body && document.body.dataset && document.body.dataset.kiosk === 'true') return true;
    } catch (_) {}
    try {
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        if (params.get('kiosk') === 'true') return true;
      }
    } catch (_) {}
    return false;
  }, []);

  const colorDef = configData?.SupportedModels?.Options?.Option1;
  const colorValues = Array.isArray(colorDef?.values) ? colorDef.values : [];
  const colorMetaByValue = React.useMemo(() => {
    const map = new Map();
    colorValues.forEach((v) => {
      if (!v || v.value == null) return;
      map.set(String(v.value).toLowerCase(), v);
    });
    return map;
  }, [colorValues]);

  if (!showExportedImages) return null;

  const items = Array.isArray(allViewsImages) && allViewsImages.length > 0
    ? allViewsImages
    : (exportedImages ? [exportedImages] : []);

  const firstItem = items && items.length > 0 ? items[0] : null;
  const penMeta = firstItem?.meta?.pen || null;
  const firstItemPreview = firstItem?.preview || null;

  const counts = penMeta?.counts && typeof penMeta.counts === 'object' ? penMeta.counts : null;

  const getColorHex = (value) => {
    const meta = colorMetaByValue.get(String(value).toLowerCase());
    const raw = meta?.swatchHex || meta?.swatchhex || meta?.hex || meta?.color;
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    return s.startsWith('#') ? s : `#${s}`;
  };

  const getColorLabel = (value) => {
    const meta = colorMetaByValue.get(String(value).toLowerCase());
    const dn = meta?.displayName;
    if (!dn) return String(value);
    if (typeof dn === 'string') return dn;
    return dn?.en || dn?.cn || String(value);
  };

  const getViewIdRaw = (view) => {
    if (!view) return 'default';
    if (typeof view === 'string') return view;
    if (typeof view === 'object' && view.id) return view.id;
    return String(view);
  };

  const getViewDisplayName = (view) => {
    const known = new Set(['front', 'back', 'left', 'right', 'top', 'bottom', 'default', 'card', 'box']);

    if (view && typeof view === 'object') {
      if (view.name) {
        if (typeof view.name === 'object') {
          return view.name[currentLang] || view.name.en || view.name.cn || view.id || '';
        }
        if (typeof view.name === 'string') return view.name;
      }
      if (view.id) {
        const key = String(view.id).toLowerCase();
        if (known.has(key)) return t(`views.${key}`, view.id);
        return view.id;
      }
    }

    const raw = getViewIdRaw(view);
    const key = (raw || 'default').toString().toLowerCase();
    if (known.has(key)) return t(`views.${key}`, raw);
    return raw;
  };

  return (
    <>
      <div className={`dpep-backdrop ${showExportedImages ? 'show' : ''}`} role="presentation" aria-hidden="true" />

      <div className={`dpep-panel ${showExportedImages ? 'show' : ''}`} onClick={(e) => e.stopPropagation()}>
        {isKiosk && isExporting ? (
          <div className="dpep-kiosk-loading" aria-hidden="true">
            <div className="dpep-kiosk-loading-card">
              <div className="dpep-kiosk-loading-spinner" />
              <div className="dpep-kiosk-loading-text">
                {statusMessage || t('status.exporting', 'Exporting...')}
              </div>
            </div>
          </div>
        ) : null}

        <div className="dpep-header">
          <div className="dpep-title">{t('export.title', 'Export')}</div>
          {isKiosk ? null : (
            <button className="dpep-close" onClick={() => setShowExportedImages(false)} aria-label={t('export.close', 'Close')}>
              ×
            </button>
          )}
        </div>

        {isExporting && (
          <div className="dpep-status">
            {statusMessage || t('status.exporting', 'Exporting...')}
          </div>
        )}

        {!isKiosk && !isExporting && statusMessage ? (
          <div className="dpep-status">{statusMessage}</div>
        ) : null}

        <div className="dpep-content">
          {items.map((it, idx) => {
            const preview = it?.preview || null;
            const design = it?.design || null;
            const viewName = getViewDisplayName(it?.view) || `View ${idx + 1}`;
            const viewId = getViewIdRaw(it?.view);

            return (
              <div key={`${viewId}-${it?.designIndex ?? idx}`} style={{ marginBottom: idx < items.length - 1 ? 14 : 0 }}>
                {!isKiosk || items.length > 1 ? (
                  <div style={{ fontWeight: 700, margin: '6px 2px 10px 2px' }}>{viewName}</div>
                ) : null}
                {isKiosk ? (
                  <div className="dpep-grid dpep-grid-kiosk">
                    <div className="dpep-card">
                      {preview ? (
                        <img className="dpep-img" src={preview} alt="Preview" />
                      ) : (
                        <div className="dpep-placeholder" />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="dpep-grid">
                    <div className="dpep-card">
                      <div className="dpep-card-title">{t('export.preview', 'Preview')}</div>
                      {preview ? (
                        <img className="dpep-img" src={preview} alt="Preview" />
                      ) : (
                        <div className="dpep-placeholder" />
                      )}
                    </div>

                    <div className="dpep-card">
                      <div className="dpep-card-title">{t('export.design', 'Design')}</div>
                      {design ? (
                        <div className="dpep-checker">
                          <img className="dpep-img" src={design} alt="Design" />
                        </div>
                      ) : (
                        <div className="dpep-placeholder" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {!isKiosk && counts ? (
            <div className="dpep-legend">
              <div className="dpep-legend-title">{t('export.selectedColors', 'Selected Colors')}</div>
              <div className="dpep-legend-items">
                {Object.entries(counts)
                  .filter(([, q]) => Number(q) > 0)
                  .map(([color, q]) => (
                    <div key={color} className="dpep-legend-item">
                      {getColorHex(color) ? (
                        <span
                          className="dpep-legend-swatch"
                          style={{ background: getColorHex(color) }}
                          aria-hidden="true"
                        />
                      ) : null}
                      <span className="dpep-legend-color">{getColorLabel(color)}</span>
                      <span className="dpep-legend-qty">× {Number(q)}</span>
                    </div>
                  ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="dpep-footer">
          {isKiosk ? (
            <>
              <button className="dpep-secondary" onClick={() => setShowExportedImages(false)} disabled={isExporting}>
                {t('general.back', 'Back')}
              </button>
              {typeof onAddToCart === 'function' ? (
                <button className="dpep-primary" onClick={onAddToCart} disabled={isExporting}>
                  {t('export.addToCart', 'Add to Cart')}
                </button>
              ) : null}
            </>
          ) : (
            typeof onAddToCart === 'function' ? (
              <button className="dpep-primary" onClick={onAddToCart} disabled={isExporting}>
                {t('export.addToCart', 'Add to Cart')}
              </button>
            ) : null
          )}

          {isTestMode && firstItemPreview ? (
            <a className="dpep-secondary" href={firstItemPreview} download>
              {t('export.downloadPreview', 'Download Preview')}
            </a>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default DeliverPrintExportPanel;
